import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { FieldLabel, SelectField } from "./PlanShared";
import { TYPES_VOYAGE, BUDGET_CATEGORIES } from "./planConstants";

export function Step1Trip({ data, set, onNext }) {
  const [showRepartition, setShowRepartition] = useState(false);
  const canGoNext = data.ville_depart && data.date_depart && data.date_retour && data.date_depart <= data.date_retour;

  return (
    <div className="animate-fade-in-up mt-8 space-y-6">
      <div>
        <p className="section-label">Étape 1</p>
        <h2 className="text-4xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
          Le voyage
        </h2>
        <p className="text-sm text-[#5C554E] mt-1 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          Votre conseiller s'occupe de tout — remplissez ce que vous savez, laissez le reste vide.
        </p>
      </div>

      <div className="card-surface space-y-6">
        <div>
          <FieldLabel>Ville de départ *</FieldLabel>
          <input className="input-field" placeholder="Paris, Lyon, Marseille..." value={data.ville_depart} onChange={(e) => set("ville_depart", e.target.value)} required data-testid="input-ville-depart" />
        </div>
        <div>
          <FieldLabel>Destination <span className="normal-case text-[#9A8F86]">(vide = votre conseiller suggère)</span></FieldLabel>
          <input className="input-field" placeholder="Tokyo, Bali, Marrakech..." value={data.destination} onChange={(e) => set("destination", e.target.value)} data-testid="input-destination" />
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <FieldLabel>Date de départ *</FieldLabel>
            <input type="date" className="input-field-box" value={data.date_depart} min={new Date().toISOString().split("T")[0]} onChange={(e) => set("date_depart", e.target.value)} required data-testid="input-date-depart" />
          </div>
          <div>
            <FieldLabel>Date de retour *</FieldLabel>
            <input type="date" className="input-field-box" value={data.date_retour} min={data.date_depart || new Date().toISOString().split("T")[0]} onChange={(e) => set("date_retour", e.target.value)} required data-testid="input-date-retour" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <FieldLabel>Adultes</FieldLabel>
            <SelectField value={data.nb_adultes} onChange={(v) => set("nb_adultes", parseInt(v))} options={[1,2,3,4,5,6]} testid="select-adultes" />
          </div>
          <div>
            <FieldLabel>Enfants</FieldLabel>
            <SelectField value={data.nb_enfants} onChange={(v) => set("nb_enfants", parseInt(v))} options={[0,1,2,3,4]} testid="select-enfants" />
          </div>
        </div>

        <div>
          <FieldLabel>Budget du voyage</FieldLabel>
          <div className="relative mb-2">
            <input
              type="number"
              min={100}
              max={500000}
              step={100}
              placeholder="ex: 9 000"
              value={data.budget_montant}
              onChange={(e) => set("budget_montant", e.target.value === "" ? "" : parseInt(e.target.value))}
              className="w-full border border-[#DCD4C6] bg-[#FAF7F3] px-4 py-2.5 pr-10 text-sm focus:outline-none focus:border-[#C17F59] transition-colors"
              style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px" }}
              data-testid="input-budget-montant"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#9A8F86]" style={{ fontFamily: "'Lora', Georgia, serif" }}>€</span>
          </div>
          <div className="flex gap-1.5 mb-2">
            {[{ v: "total", label: "Total groupe" }, { v: "par_personne", label: "Par personne" }].map(({ v, label }) => (
              <button
                key={v}
                type="button"
                onClick={() => set("budget_type", v)}
                className={`flex-1 py-2 text-xs border transition-all ${data.budget_type === v ? "bg-[#C17F59] text-[#F9F6F0] border-[#C17F59]" : "border-[#DCD4C6] text-[#5C554E] hover:border-[#C17F59]"}`}
                style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px" }}
                data-testid={`budget-type-${v}`}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-[#5C554E] mb-3" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            <input
              type="checkbox"
              checked={data.vols_inclus}
              onChange={(e) => set("vols_inclus", e.target.checked)}
              className="accent-[#C17F59] w-3.5 h-3.5"
              data-testid="checkbox-vols-inclus"
            />
            Vols déjà réservés — ce budget est pour les dépenses sur place uniquement
          </label>
          <button
            type="button"
            onClick={() => setShowRepartition(!showRepartition)}
            className="w-full text-xs text-[#C17F59] border border-[#C17F59]/30 py-2 hover:bg-[#C17F59]/5 transition-colors flex items-center justify-center gap-1.5"
            style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px" }}
            data-testid="btn-toggle-repartition"
          >
            {showRepartition ? "▲" : "▼"} Répartition par poste {showRepartition ? "" : "(optionnel)"}
          </button>
          {showRepartition && (
            <div className="mt-3 space-y-3 p-3 border border-[#DCD4C6] bg-[#FAF7F3]" style={{ borderRadius: "2px" }}>
              {(() => {
                const total = BUDGET_CATEGORIES.reduce((s, c) => s + (data[c.key] || 0), 0);
                return (
                  <>
                    {BUDGET_CATEGORIES.map(({ key, label, color }) => (
                      <div key={key}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs" style={{ fontFamily: "'Lora', Georgia, serif", color: "#2B2623" }}>{label}</span>
                          <span className="text-xs font-semibold" style={{ color, fontFamily: "'Lora', Georgia, serif" }}>{data[key] || 0}%</span>
                        </div>
                        <input
                          type="range"
                          min={0} max={70} step={5}
                          value={data[key] || 0}
                          onChange={(e) => set(key, parseInt(e.target.value))}
                          className="w-full h-1.5 appearance-none cursor-pointer"
                          style={{ accentColor: color }}
                          data-testid={`slider-${key}`}
                        />
                      </div>
                    ))}
                    <div className={`text-xs text-right pt-1 border-t border-[#DCD4C6] ${total === 100 ? "text-emerald-600" : "text-[#9A8F86]"}`} style={{ fontFamily: "'Lora', Georgia, serif" }}>
                      Total : {total}%{total === 100 ? " ✓" : ""}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <div>
          <FieldLabel>Type de voyage</FieldLabel>
          <SelectField value={data.type_voyage} onChange={(v) => set("type_voyage", v)} options={TYPES_VOYAGE} testid="select-type-voyage" />
        </div>
      </div>

      <button onClick={onNext} disabled={!canGoNext} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="step1-next-btn">
        Continuer <ChevronRight size={15} strokeWidth={2} />
      </button>
    </div>
  );
}
